import React, { useState } from "react";
import { message } from "antd";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Fade,
  Slide,
} from "@mui/material";
import imageCompression from "browser-image-compression";
import { API_BASE_URL, getAuthHeaders, getUploadAuthHeaders } from "../API/Api";

const PostBrand = () => {
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleReset = () => {
    setName("");
    setImageUrl("");
    setPreviewUrl("");
  };

  const handleImageUpload = async (file) => {
    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setUploading(true);

    try {
      const compressedFile = await imageCompression(file, {
        maxSizeMB: 1,
        maxWidthOrHeight: 512,
        useWebWorker: true,
        initialQuality: 0.85,
        alwaysKeepResolution: true,
      });

      const uploadFormData = new FormData();
      uploadFormData.append("image", compressedFile);

      const uploadRes = await fetch(`${API_BASE_URL}/admin/images/upload`, {
        method: "POST",
        headers: getUploadAuthHeaders(),
        body: uploadFormData,
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed. Status: ${uploadRes.status}`);
      }

      const uploadData = await uploadRes.json();
      const uploadedPath =
        uploadData?.path || uploadData?.data?.path || uploadData?.url || "";

      if (!uploadedPath) {
        throw new Error("Upload response missing file path");
      }

      setImageUrl(uploadedPath);
      // message.success("Image uploaded");
    } catch (error) {
      console.error("Image compression/upload failed:", error);
      message.error("Failed to upload image");
      setImageUrl("");
    } finally {
      URL.revokeObjectURL(localPreview);
      setUploading(false);
    }
  };

  const onChangeUpload = (e) => {
    if (e?.target?.files?.[0]) {
      handleImageUpload(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      message.warning("Please enter brand name");
      return;
    }

    setSubmitting(true);
    try {
      console.log("name:", name.trim());
      console.log("image_url:", imageUrl || null);

      const payload = {
        name: name.trim(),
        image: imageUrl || null,
      };

      const res = await fetch(`${API_BASE_URL}/admin/brands`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      message.success("Brand created successfully");
      window.location.href = "/admin/brands";
    } catch (error) {
      console.error("Error creating brand:", error);
      message.error("Failed to create brand");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ maxWidth: 1000, mx: "auto", my: 5, px: 2 }}>
        <Slide direction="up" in timeout={400}>
          <Paper elevation={6} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3 }}>
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Create Brand
                </Typography>
              </Box>

              <Box
                sx={{
                  mb: 4,
                  p: 2,
                  border: "1px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  textAlign: "center",
                  backgroundColor: "grey.50",
                }}
              >
                <Box
                  sx={{
                    width: 180,
                    height: 180,
                    mx: "auto",
                    mb: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "common.white",
                  }}
                >
                  {previewUrl || imageUrl ? (
                    <Box
                      component="img"
                      src={previewUrl || imageUrl}
                      alt="Brand preview"
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No image
                    </Typography>
                  )}
                </Box>
                <input
                  id="brand-logo-upload"
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={onChangeUpload}
                />
                <label htmlFor="brand-logo-upload">
                  <Button variant="outlined" component="span">
                    {uploading ? "Uploading..." : "Upload Brand Image"}
                  </Button>
                </label>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Brand Name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{ display: "flex", gap: 2, justifyContent: "center" }}
                  >
                    <Button variant="outlined" onClick={handleReset}>
                      Reset
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => (window.location.href = "/admin/brands")}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Slide>
      </Box>
    </Fade>
  );
};

export default PostBrand;
